import React from "react";
import styled from "styled-components/macro";
import { getABTestOverride, setABTestOverride } from "queries/users/useABTest";
import { Tooltip } from "@mui/material";
import { useEvent } from "react-use";
import { EVENT } from "tools/events";

const Indicator = styled.div<{ $enabled: boolean }>`
  position: fixed;
  bottom: 45px;
  right: 5px;
  z-index: 9999;
  background: ${({ $enabled }) => ($enabled ? "#4caf50" : "#f44336")};
  color: white;
  border-radius: 50%;
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  font-size: 12px;
  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: scale(1.15);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ABTestIndicator: React.FC = () => {
  const [overrideValue, setOverrideValue] = React.useState(getABTestOverride());
  const longPressTimer = React.useRef<NodeJS.Timeout | null>(null);
  const isLongPress = React.useRef(false);

  // Listen for AB test override changes
  useEvent(EVENT.abtestOverrideChange, () => {
    setOverrideValue(getABTestOverride());
  });

  const handleMouseDown = () => {
    isLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      isLongPress.current = true;
      // Toggle between true and false
      const newValue = !getABTestOverride();
      setABTestOverride(newValue);
      setOverrideValue(newValue);
    }, 500); // 500ms for long press
  };

  const handleMouseUp = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }

    // If not a long press, remove override
    if (!isLongPress.current) {
      setABTestOverride(null);
      setOverrideValue(null);
    }
  };

  const handleMouseLeave = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  };

  if (overrideValue === null) return null;

  return (
    <Tooltip title="Click: remove | Hold: toggle" placement="left">
      <Indicator
        $enabled={overrideValue}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleMouseDown}
        onTouchEnd={handleMouseUp}
      >
        AB
      </Indicator>
    </Tooltip>
  );
};

export default ABTestIndicator;
